import { Checkout } from '@df/checkout/checkout.interfaces';
import { ApplePay } from '@df/module-apple-pay/apple-pay.namespace';
import { GooglePay } from '@df/module-google-pay/google-pay.namespace';
import { Paypal } from '@df/module-paypal/paypal.namespace';
import mapValues from 'lodash-es/mapValues';

declare global {
    interface Window {
        braintree?: {
            client?: Braintree.IClient;
            hostedFields?: Braintree.IHostedFields;
            threeDSecure?: Braintree.IThreeDS;
            paypalCheckout?: Braintree.IPaypalCheckout;
            dataCollector?: Braintree.IDataCollector;
            localPayment?: Braintree.ILocalPayment;
            applePay?: Braintree.IApplePay;
            googlePayment?: Braintree.IGooglePay;
        };
    }
}

export namespace Braintree {
    let version = '3.62.1';

    const scriptNames = {
        CLIENT: 'client.min.js',
        HOSTED_FIELDS: 'hosted-fields.min.js',
        THREE_DS: 'three-d-secure.min.js',
        PAYPAL_CHECKOUT: 'paypal-checkout.min.js',
        DATA_COLLECTOR: 'data-collector.min.js',
        LOCAL_PAYMENT: 'local-payment.min.js',
        APPLE_PAY: 'apple-pay.min.js',
        GOOGLE_PAY: 'google-payment.min.js'
    };
    // eslint-disable-next-line @typescript-eslint/naming-convention
    export const Scripts = {
        ...mapValues(scriptNames, value => `https://js.braintreegateway.com/web/${version}/js/${value}`),
        DROPIN: 'https://js.braintreegateway.com/web/dropin/1.21.0/js/dropin.min.js'
    };

    export function assureVersion(neededVersion: string): void {
        if (version.localeCompare(neededVersion, undefined, { numeric: true, sensitivity: 'base' }) < 0) {
            version = neededVersion;
        }
        for (const key of Object.keys(scriptNames)) {
            Scripts[key] = `https://js.braintreegateway.com/web/${version}/js/${scriptNames[key]}`;
        }
    }

    export enum Endpoints {
        GET_NONCE = '/braintree/getVaultNonce',
        UPDATE_NONCE = '/braintree/updateVaultNonce'
    }

    ////////////////////////////////////////////
    ///////////
    /////////// Payment methods
    ///////////
    ////////////////////////////////////////////

    export enum Methods {
        CreditCard = 'braintree',
        PaypalCheckout = 'braintree_paypal',
        ApplePay = 'braintree_applepay',
        GooglePay = 'braintree_googlepay'
    }

    export enum LocalPaymentMethods {
        BANCONTACT = 'braintree_local_payment__bancontact',
        EPS = 'braintree_local_payment__eps',
        GIROPAY = 'braintree_local_payment__giropay',
        IDEAL = 'braintree_local_payment__ideal',
        SOFORT = 'braintree_local_payment__sofort',
        MYBANK = 'braintree_local_payment__mybank',
        P24 = 'braintree_local_payment__p24',
        SEPA = 'braintree_local_payment__sepa'
    }

    export enum LocalPaymentTypes {
        BANCONTACT = 'bancontact',
        EPS = 'eps',
        GIROPAY = 'giropay',
        IDEAL = 'ideal',
        SOFORT = 'sofort',
        MYBANK = 'mybank',
        P24 = 'p24',
        SEPA = 'sepa'
    }

    export enum TokenizePayloadTypes {
        CREDIT_CARD = 'CreditCard',
        APPLE_PAY = 'ApplePayCard',
        GOOGLE_PAY = 'AndroidPayCard'
    }

    export interface IPaymentMethod extends Checkout.IPaymentMethod {
        config: IPaymentMethodConfig;
        code: Methods;
    }

    export interface IPaymentMethodConfig {
        clientToken: string;
    }

    export interface ICreditCardPaymentMethod extends IPaymentMethod {
        config: ICreditCardPaymentMethodConfig;
        code: Methods.CreditCard;
    }

    export interface ICreditCardPaymentMethodConfig extends IPaymentMethodConfig {
        useCvv?: boolean;
        ['3ds']?: ICreditCardPaymentMethodConfig3ds;
        vault?: ICreditCardPaymentMethodConfigVault;
    }

    export interface ICreditCardPaymentMethodConfig3ds {
        thresholdAmount: number;
        specificCountries: string[];
    }

    export interface ICreditCardPaymentMethodConfigVault {
        useCvv: boolean;
    }

    export interface ILocalPaymentPaymentMethod extends IPaymentMethod {
        group: 'braintree_local_payment';
        config: ILocalPaymentPaymentMethodConfig;
    }

    export interface ILocalPaymentPaymentMethodConfig extends IPaymentMethodConfig {
        merchantId: string;
    }

    export interface IPaypalCheckoutPaymentMethod extends IPaymentMethod {
        code: Methods.PaypalCheckout;
        config: IPaypalCheckoutPaymentMethodConfig;
    }

    export interface IPaypalCheckoutPaymentMethodConfig extends IPaymentMethodConfig {
        locale?: Paypal.ButtonLocale;
        canUseCredit?: boolean;
        environment?: 'sandbox' | 'production';
        vault?: [] | { [key: string]: any };
        style?: Paypal.IButtonStyle;
    }

    interface IApi<T extends IApiCreateOptions = IApiCreateOptions, D extends IApiInstance = IApiInstance> {
        version: string;
        create(options: T): Promise<D>;
    }

    interface IApiCreateOptions {
        // we can use client or authorization directly, one of them is required
        authorization?: string;
        client?: IClientInstance;
    }

    interface IApiInstance {
        // cleanly remove anything set up by create
        teardown(callback: () => void): void;
        teardown(): Promise<undefined>;
    }

    export interface IPaymentSubmissionData<
        T extends Methods | LocalPaymentMethods | 'vault',
        D extends IPaymentSubmissionAdditionalData = IPaymentSubmissionAdditionalData
    > extends Checkout.ISubmissionPaymentData {
        method: T;
        additional_data: D;
    }

    export interface IPaymentSubmissionAdditionalData {
        payment_method_nonce: string;
    }

    export interface ITokenizePayload<T extends TokenizePayloadTypes, D extends {}> extends ITokenizePayloadLike<D> {
        // A human-readable description.
        description: string;
        type: T;
        binData: ITokenizePayloadBinData;
    }

    export interface ITokenizePayloadLike<D extends {}> {
        // The payment method nonce.
        nonce: string;
        // Additional account details.
        details?: D;
    }

    ////////////////////////////////////////////
    ///////////
    /////////// Client
    ///////////
    ////////////////////////////////////////////

    export interface IClient extends IApi<IClientOptions, IClientInstance> {}

    export interface IClientOptions {
        authorization: string;
    }

    export interface IClientInstance extends IApiInstance {}

    ////////////////////////////////////////////
    ///////////
    /////////// 3DS
    ///////////
    ////////////////////////////////////////////

    export interface IThreeDS extends IApi<IThreeDSOptions, IThreeDSInstance> {}

    export interface IThreeDSOptions extends IApiCreateOptions {
        version: IThreeDSVersion;
    }

    // The version of 3D Secure to use. Possible options:
    // 1                    - The legacy 3D Secure v1.0 integration.
    // 2                    - A 3D Secure v2.0 integration that uses a modal to host the 3D Secure iframe.
    // 2-bootstrap3-modal   - A 3D Secure v2.0 integration that uses a modal styled with Bootstrap 3 styles to host the 3D Secure iframe.
    //                        Requires having the Bootstrap 3 script files and stylesheets on your page.
    // 2-inline-iframe      - A 3D Secure v2.0 integration that provides the authentication iframe directly to the merchant.
    export type IThreeDSVersion = 1 | 2 | '2-bootstrap3-modal' | '2-inline-iframe';

    export interface IThreeDSInstance extends IApiInstance {
        // Cancel the 3DS flow and return the verification payload if available.
        // If using 3D Secure version 2, this will not close the UI of the authentication modal.
        // It is recommended that this method only be used in the lookup-complete event.
        cancelVerifyCard(): Promise<undefined>;
        off(
            eventName: ThreeDSEventType.AuthenticationIframeAvailable,
            handler: (event: IThreeDSAuthenticationIframeAvailableEvent, next: () => void) => void
        ): void;
        off(eventName: ThreeDSEventType.LookupComplete, handler: (data: IThreeDSVerificationData) => void): void;
        on(
            eventName: ThreeDSEventType.AuthenticationIframeAvailable,
            handler: (event: IThreeDSAuthenticationIframeAvailableEvent, next: () => void) => void
        ): void;
        on(eventName: ThreeDSEventType.LookupComplete, handler: (data: IThreeDSVerificationData, next: () => void) => void): void;
        // Launch the 3D Secure login flow, returning a nonce payload.
        verifyCard(options: IThreeDSVerifyCardOptions): Promise<IThreeDSVerifyPayload>;
    }

    export enum ThreeDSEventType {
        LookupComplete = 'lookup-complete',
        AuthenticationIframeAvailable = 'authentication-iframe-available'
    }

    export interface IThreeDSAuthenticationIframeAvailableEvent {
        element: HTMLElement;
    }

    export interface IThreeDSVerificationData {
        // When true, the user will be presented with a 3D Secure challenge when calling next in the lookup-complete event.
        requiresUserAuthentication: string;
        // Contains liability shift details.
        threeDSecureInfo: {
            // Indicates whether the card was eligible for 3D Secure.
            liabilityShiftPossible: boolean;
            // Indicates whether the liability for fraud has been shifted away from the merchant.
            liabilityShifted: boolean;
        };
        paymentMethod: IThreeDSVerifyPayload;
        lookup: {
            threeDSecureVersion: string;
        };
    }

    export interface IThreeDSVerifyPayload {
        // The new payment method nonce produced by the 3D Secure lookup.
        // The original nonce passed into verifyCard was consumed. This new nonce should be used to transact on your server.
        nonce: string;
        binData: ITokenizePayloadBinData;
        details: IPayloadDetails;
        description: string;
        threeDSecureInfo: {
            liabilityShiftPossible: boolean;
            liabilityShifted: boolean;
        };
    }

    export interface IThreeDSVerifyCardOptions {
        nonce: string;
        bin?: string;
        amount: string;
        // If set to true, an authentication challenge will be forced if possible.
        challengeRequested?: boolean;
        collectDeviceData?: boolean;
        // If set to true, an exemption to the authentication challenge will be requested.
        exemptionRequested?: boolean;
        email?: string;
        billingAddress?: IThreeDSVerifyCardOptionsBillingAddress;
        additionalInformation?: IThreeDSVerifyCardOptionsAdditionalInformation;
    }

    export interface IThreeDSVerifyCardOptionsAdditionalInformation {
        // 01 250x400
        // 02 390x400
        // 03 500x600
        // 04 600x400
        // 05 Full page
        acsWindowSize?: '01' | '02' | '03' | '04' | '05';
    }

    export interface IThreeDSVerifyCardOptionsBillingAddress {
        givenName?: string;
        surname?: string;
        phoneNumber?: string;
        streetAddress?: string;
        extendedAddress?: string;
        line3?: string;
        // The locality (city) name associated with the billing address.
        locality?: string;
        // The 2 letter code for US states, and the equivalent for other countries.
        region?: string;
        postalCode?: string;
        countryCodeAlpha2?: string;
    }

    ////////////////////////////////////////////
    ///////////
    /////////// Hosted fields
    ///////////
    ////////////////////////////////////////////

    export interface IHostedFields extends IApi<IHostedFieldsOptions, IHostedFieldsInstance> {}

    export interface IHostedFieldsOptions extends IApiCreateOptions {
        styles?: any;
        fields: IHostedFieldsFieldOptions;
    }

    export interface IHostedFieldsInstance extends IApiInstance {
        // returns state of hosted fields instance
        getState(): IHostedFieldsInstanceState;
        // tokenizes fields and returns a nonce payload
        tokenize(options?: ITokenizeOptions): Promise<IHostedFieldsTokenizePayload>;
        // Subscribes a handler function to a named event.
        on(eventType: HostedFieldEventType, handler: (event: IHostedFieldsInstanceState) => void): void;
        // Unsubscribes the handler function to a named event.
        off(eventType: HostedFieldEventType, handler: (event: IHostedFieldsInstanceState) => void): void;
    }

    export interface IHostedFieldsInstanceState {
        fields: IHostedFieldsInstanceStateFields;
        cards: IHostedFieldsInstanceStateCard[];
        // The name of the field associated with an event. This will not be included if returned by getState.
        emittedBy?: keyof IHostedFieldsInstanceStateFields;
    }

    export interface IHostedFieldsInstanceStateFields {
        number?: IHostedFieldsInstanceStateFieldsField;
        expirationDate?: IHostedFieldsInstanceStateFieldsField;
        expirationMonth?: IHostedFieldsInstanceStateFieldsField;
        expirationYear?: IHostedFieldsInstanceStateFieldsField;
        cvv?: IHostedFieldsInstanceStateFieldsField;
        postalCode?: IHostedFieldsInstanceStateFieldsField;
    }

    export interface IHostedFieldsInstanceStateFieldsField {
        isEmpty: boolean;
        isValid: boolean;
        isPotentiallyValid: boolean;
        isFocused: boolean;
        container: HTMLElement;
    }

    export interface IHostedFieldsInstanceStateCard {
        niceType: string;
        type: string;
        patterns: number[];
        gaps: number[];
        lengths: number[];
        code: { name: 'CVV' | 'CID' | 'CVC'; size: 3 | 4 };
    }

    export interface ITokenizeOptions {
        vault?: boolean;
        cardholderName?: string;
    }

    export interface IHostedFieldsTokenizePayload
        extends ITokenizePayload<TokenizePayloadTypes.CREDIT_CARD, IHostedFieldsTokenizePayloadDetails> {}

    export interface IHostedFieldsTokenizePayloadLike extends ITokenizePayloadLike<IHostedFieldsTokenizePayloadDetails> {}
    export interface IHostedFieldsTokenizePayloadDetails extends IPayloadDetails {
        // The BIN number of the card.
        bin: string;
        expirationMonth: string;
        expirationYear: string;
    }

    export interface IPayloadDetails {
        lastFour: string;
        lastTwo: string;
        // Type of card, ex: Visa, MasterCard.
        cardType: string;
    }

    export interface ITokenizePayloadBinData {
        commercial: ITokenizePayloadBinDataValue;
        countryOfIssuance: string;
        debit: ITokenizePayloadBinDataValue;
        durbinRegulated: ITokenizePayloadBinDataValue;
        healthcare: ITokenizePayloadBinDataValue;
        issuingBank: ITokenizePayloadBinDataValue;
        payroll: ITokenizePayloadBinDataValue;
        prepaid: ITokenizePayloadBinDataValue;
        productId: string;
    }

    export type ITokenizePayloadBinDataValue = 'Yes' | 'No' | 'Unknown';

    export enum HostedFieldEventType {
        Blur = 'blur',
        Focus = 'focus',
        Empty = 'empty',
        NotEmpty = 'notEmpty',
        CardTypeChange = 'cardTypeChange',
        ValidityChange = 'validityChange',
        InputSubmitRequest = 'inputSubmitRequest'
    }

    export interface IHostedFieldsFieldOptions {
        // A field for card number.
        number?: IHostedFieldsField;
        // A field for expiration date in MM/YYYY or MM/YY format.
        // This should not be used with the expirationMonth and expirationYear properties.
        expirationDate?: IHostedFieldsField;
        // A field for expiration month in MM format. This should be used with the expirationYear property.
        expirationMonth?: IHostedFieldsField;
        // A field for expiration year in YYYY or YY format. This should be used with the expirationMonth property.
        expirationYear?: IHostedFieldsField;
        // A field for 3 or 4 digit card verification code (like CVV or CID).
        // If you wish to create a CVV-only payment method nonce to verify a card already stored in your Vault,
        // omit all other fields to only collect CVV.
        cvv?: IHostedFieldsField;
        // A field for postal or region code.
        postalCode?: IHostedFieldsField;
    }

    export interface IHostedFieldsField {
        // A CSS selector to find the container where the hosted field will be inserted.
        selector: string;
        // Will be used as the `placeholder` attribute of the input.
        // If `placeholder` is not natively supported by the browser, it will be polyfilled.
        placeholder?: string;
        // Will be used as the `type` attribute of the input. To mask `cvv` input, for instance, `type: "password"` can be used.
        type?: string;
        // Enable or disable automatic formatting on this field.
        formatInput?: boolean;
        // Enable or disable input masking when input is not focused.
        // If set to `true` instead of an object, the defaults for the `maskInput` parameters will be used.
        maskInput?: boolean | IHostedFieldsFieldMaskInput;
        // If truthy, this field becomes a `<select>` dropdown list.
        // This can only be used for `expirationMonth` and `expirationYear` fields.
        // An array of 12 strings, one per month. This can only be used for the `expirationMonth` field.
        // For example, the array can look like `['01 - January', '02 - February', ...]`.
        select?: boolean | { options: string[] };
        maxCardLength?: number;
        maxlength?: number;
        minlength?: number;
        prefill?: string;
        rejectUnsupportedCards?: boolean;
    }

    export interface IHostedFieldsFieldMaskInput {
        // The character to use when masking the input.
        character?: string;
        // Only applicable for the credit card field. Whether or not to show the last 4 digits of the card when masking.
        showLastFour?: boolean;
    }

    export interface IHostedFieldsPaymentSubmissionData
        extends IPaymentSubmissionData<Methods.CreditCard, IHostedFieldsPaymentSubmissionAdditionalData> {}

    export interface IHostedFieldsPaymentSubmissionAdditionalData extends IPaymentSubmissionAdditionalData {
        store_cc?: boolean;
    }

    ////////////////////////////////////////////
    ///////////
    /////////// Errors
    ///////////
    ////////////////////////////////////////////

    export interface IError {
        name: 'BraintreeError';
        code: string;
        message: string;
        type: 'CUSTOMER' | 'MERCHANT' | 'NETWORK' | 'INTERNAL' | 'UNKNOWN';
        details?: IErrorDetails;
    }

    export interface IErrorDetails {
        invalidFieldKeys: (keyof IHostedFieldsFieldOptions)[];
        invalidFields: {
            number?: HTMLElement;
            expirationDate?: HTMLElement;
            expirationMonth?: HTMLElement;
            expirationYear?: HTMLElement;
            cvv?: HTMLElement;
            postalCode?: HTMLElement;
        };
    }

    ////////////////////////////////////////////
    ///////////
    /////////// Vault nonce methods
    ///////////
    ////////////////////////////////////////////

    export interface IVaultPaymentMethodNonce {
        paymentMethodNonce: string;
        details?: IHostedFieldsTokenizePayloadDetails;
    }

    export interface IVaultPaymentSubmissionData extends IPaymentSubmissionData<'vault', IVaultPaymentSubmissionAdditionalData> {}

    export interface IVaultPaymentSubmissionAdditionalData extends IPaymentSubmissionAdditionalData {
        public_hash: string;
    }

    ////////////////////////////////////////////
    ///////////
    /////////// Paypal checkout
    ///////////
    ////////////////////////////////////////////

    export interface IPaypalCheckout extends IApi<IPaypalCheckoutCreateOptions, IPaypalCheckoutInstance> {
        // if paypal checkout supports current browser
        isSupported(): boolean;
    }

    export interface IPaypalCheckoutCreateOptions extends IApiCreateOptions {
        merchantAccountId?: string;
    }

    export interface IPaypalCheckoutInstance extends IApiInstance {
        createPayment(options: IPaypalCheckoutCreatePaymentOptions<PaypalCheckoutFlow>): Promise<string>;
        createPayment(
            options: IPaypalCheckoutCreatePaymentOptions<PaypalCheckoutFlow.CHECKOUT>,
            callback: (paymentId: string) => void
        ): void;
        createPayment(
            options: IPaypalCheckoutCreatePaymentOptions<PaypalCheckoutFlow.VAULT>,
            callback: (billingToken: string) => void
        ): void;
        getClientId(callback: (id: string) => void): void;
        getClientId(): Promise<string>;
        tokenizePayment(options: IPaypalCheckoutTokenizeOptions): Promise<IPaypalCheckoutTokenizePayload>;
        tokenizePayment(options: IPaypalCheckoutTokenizeOptions, callback: (payload: IPaypalCheckoutTokenizePayload) => void): void;
        loadPayPalSDK(options?: IPaypalSdkLoadPayPalSDKOptions): Promise<void>;
    }

    export interface IPaypalSdkLoadPayPalSDKOptions {
        intent?: PaypalCheckoutIntent;
        currency?: string;
        commit?: boolean;
        vault?: boolean;
        'enable-funding'?: string;
        'disable-funding'?: string;
    }

    export interface IPaypalCheckoutCreatePaymentCheckoutOptions extends IPaypalCheckoutCreatePaymentOptions<PaypalCheckoutFlow.CHECKOUT> {
        amount: string | number;
        currency: string;
        vaultInitiatedCheckoutPaymentMethodToken?: string;
    }

    export interface IPaypalCheckoutCreatePaymentVaultOptions extends IPaypalCheckoutCreatePaymentOptions<PaypalCheckoutFlow.VAULT> {}

    export interface IPaypalCheckoutCreatePaymentOptions<T extends PaypalCheckoutFlow> {
        flow: T;
        // authorize default
        intent?: PaypalCheckoutIntent;
        // false default
        offerCredit?: boolean;
        // required for 'checkout' flow
        amount?: string | number;
        // required for 'checkout' flow
        currency?: string;
        // mechant name in lightbox, defaults to the company name in braintree account
        displayName?: string;
        // settings in brantree account > this option > buyer > browser
        locale?: string;
        shippingOptions?: [];
        enableShippingAddress?: boolean;
        // enables you to pass already collected shipping address
        shippingAddressOverride?: IPaypalCheckoutCreatePaymentShippingAddress;
        // true default
        shippingAddressEditable?: boolean;
        // max 255 characters
        billingAgreementDescription?: string;
        landingPageType?: PaypalCheckoutLandingPageType;
    }

    export enum PaypalCheckoutFlow {
        CHECKOUT = 'checkout',
        VAULT = 'vault'
    }

    export enum PaypalCheckoutIntent {
        AUTHORIZE = 'authorize',
        ORDER = 'order',
        CAPTURE = 'capture'
    }

    export enum PaypalCheckoutLandingPageType {
        LOGIN = 'login',
        BILLING = 'billing'
    }

    export interface IPaypalCheckoutCreatePaymentShippingOption {
        label: string;
        // if option should be preselected, only 1 with 'true' allowed
        selected: boolean;
        type: PaypalCheckoutCreatePaymentShippingOptionType;
        amount: IPaypalCheckoutCreatePaymentShippingOptionAmount;
    }

    export enum PaypalCheckoutCreatePaymentShippingOptionType {
        SHIPPING = 'SHIPPING',
        PICKUP = 'PICKUP'
    }

    export interface IPaypalCheckoutCreatePaymentShippingOptionAmount {
        currency: string;
        value: string;
    }

    export interface IPaypalCheckoutCreatePaymentShippingAddress extends IPaypalCheckoutCreatePaymentAddress {
        recipientName: string;
    }

    export interface IPaypalCheckoutCreatePaymentAddress {
        line1: string;
        line2?: string;
        city: string;
        state?: string;
        postalCode: string;
        countryCode: string;
        phone?: string;
    }

    export interface IPaypalCheckoutTokenizeOptions {
        payerId: string;
        paymentId?: string;
        billingToken?: string;
        // true default
        vault?: boolean;
    }

    export interface IPaypalCheckoutTokenizePayload {
        nonce: string;
        type: string;
        details: IPaypalCheckoutTokenizePayloadDetails;
        // only for paypal credit
        creditFinancingOffered?: IPaypalCheckoutTokenizePayloadCreditFinancingOffered;
    }

    export interface IPaypalCheckoutTokenizePayloadDetails {
        email: string;
        payerId: string;
        firstName: string;
        lastName: string;
        countryCode: string;
        phone: string;
        shippingAddress: IPaypalCheckoutCreatePaymentShippingAddress;
        billingAddress?: IPaypalCheckoutCreatePaymentAddress;
    }

    export interface IPaypalCheckoutTokenizePayloadCreditFinancingOffered {
        totalCost: IPaypalCheckoutTokenizePayloadCreditFinancingOfferedAmount;
        term: number;
        monthlyPayment: IPaypalCheckoutTokenizePayloadCreditFinancingOfferedAmount;
        totalInterest: IPaypalCheckoutTokenizePayloadCreditFinancingOfferedAmount;
        payerAcceptance: boolean;
        cartAmountImmutable: boolean;
    }

    export interface IPaypalCheckoutTokenizePayloadCreditFinancingOfferedAmount {
        value: string;
        currency: string;
    }

    export interface IPaypalCheckoutPaymentSubmissionData
        extends IPaymentSubmissionData<Methods.PaypalCheckout, IPaypalCheckoutPaymentSubmissionAdditionalData> {}

    export interface IPaypalCheckoutPaymentSubmissionAdditionalData extends IPaymentSubmissionAdditionalData {
        device_data?: string;
    }

    ////////////////////////////////////////////
    ///////////
    /////////// Data collector
    ///////////
    ////////////////////////////////////////////

    export interface IDataCollector extends IApi<IDataCollectorCreateOptions, IDataCollectorInstance> {}

    export interface IDataCollectorCreateOptions extends IApiCreateOptions {
        useDeferredClient?: boolean;
        kount?: boolean;
        paypal?: boolean;
    }

    export interface IDataCollectorInstance extends IApiInstance {
        deviceData: string;
        rawDeviceData: {};
        /// stringify - false is default
        getDeviceData(
            options?: IDataCollectorGetDeviceDataOptions,
            stringify?: boolean,
            callback?: (deviceData: string | {}) => void
        ): void;
        getDeviceData(options?: IDataCollectorGetDeviceDataOptions, stringify?: boolean): Promise<string | {}>;
    }

    export interface IDataCollectorGetDeviceDataOptions {
        raw?: boolean;
    }

    ////////////////////////////////////////////
    ///////////
    /////////// Local payment
    ///////////
    ////////////////////////////////////////////

    export enum LocalPaymentErrors {
        PopupClosed = 'LOCAL_PAYMENT_POPUP_CLOSED',
        WindowClosed = 'LOCAL_PAYMENT_WINDOW_CLOSED',
        WindowOpenFailed = 'LOCAL_PAYMENT_WINDOW_OPEN_FAILED'
    }

    export interface ILocalPayment extends IApi<ILocalPaymentCreateOptions, ILocalPaymentInstance> {}

    export interface ILocalPaymentCreateOptions extends IApiCreateOptions {
        // A non-default merchant account ID to use for tokenization and creation of the authorizing transaction.
        // Braintree strongly recommends specifying this parameter.
        merchantAccountId?: string;
    }

    export interface ILocalPaymentInstance extends IApiInstance {
        // Closes the LocalPayment window if it is open.
        closeWindow(): void;
        // Focuses the LocalPayment window if it is open.
        focusWindow(): void;
        // Checks if required tokenizaiton parameters are available in querystring for manual toenization requests.
        hasTokenizationParams(): boolean;
        // Launches the local payment flow and returns a nonce payload. Only one local payment flow should be active at a time.
        // One way to achieve this is to disable your local payment button while the flow is open.
        startPayment(options: ILocalPaymentStartPaymentOptions, callback: (error, payload: ILocalPaymentStartPaymentPayload) => void): void;
        startPayment(options: ILocalPaymentStartPaymentOptions): Promise<ILocalPaymentStartPaymentPayload>;
        tokenize(params: ILocalPaymentTokenizeParams, callback: (error, payload: ILocalPaymentStartPaymentPayload) => void): void;
        tokenize(callback: (error, payload: ILocalPaymentStartPaymentPayload) => void): void;
        tokenize(params: ILocalPaymentTokenizeParams): Promise<ILocalPaymentStartPaymentPayload>;
    }

    export interface ILocalPaymentStartPaymentOptions {
        // Configuration for what to do when app switching back from a Bank app on a mobile device.
        fallback: {
            // The text to insert into a button to redirect back to the merchant page.
            buttonText: string;
            // The url to redirect to when the redirect button is activated.
            // Query params will be added to the url to process the data returned from the bank.
            url: string;
        };
        // The amount to authorize for the transaction.
        amount: string;
        // The currency to process the payment.
        currencyCode: string;
        // The type of local payment.
        paymentType: LocalPaymentTypes;
        // Payer email of the customer.
        email: string;
        // First name of the customer.
        givenName: string;
        // Last name of the customer.
        surname: string;
        // Phone number of the customer.
        phone: string;
        shippingAddressRequired: boolean;
        address: ILocalPaymentStartPaymentOptionsAddress;
        // A function that will be called with two parameters:
        // an object containing the paymentId and a continueCallback that must be called to launch the flow.
        // You can use method to do any preprocessing on your server before the flow begins..
        onPaymentStart(data: ILocalPaymentOnPaymentStartData, continueCallback: () => void): void;
    }

    export interface ILocalPaymentStartPaymentOptionsAddress {
        // Line 1 of the Address (eg. number, street, etc). An error will occur if this address is not valid.
        streetAddress?: string;
        // Line 2 of the Address (eg. suite, apt #, etc.). An error will occur if this address is not valid.
        extendedAddress?: string;
        // Customer's city.
        locality?: string;
        // Customer's region or state.
        region?: string;
        // Customer's postal code.
        postalCode?: string;
        // Customer's country code.
        countryCode?: string;
    }

    export interface ILocalPaymentOnPaymentStartData {
        paymentId: string;
    }

    // All options for tokenizing local payment parameters.
    // If no params are passed in, the params will be pulled off of the query string of the page
    export interface ILocalPaymentTokenizeParams {
        // The token representing the local payment. Aliased to token if btLpToken is not present.
        btLpToken?: string;
        // The payment id for the local payment. Aliased to paymentId if btLpPaymentId is not present.
        btLpPaymentId?: string;
        // The payer id for the local payment. Aliased to PayerID if btLpPayerId is not present.
        btLpPayerId?: string;
    }

    export interface ILocalPaymentStartPaymentPayload {
        nonce: string;
    }

    export interface ILocalPaymentSubmissionData extends IPaymentSubmissionData<LocalPaymentMethods> {}

    ////////////////////////////////////////////
    ///////////
    /////////// Apple Pay
    ///////////
    ////////////////////////////////////////////

    export interface IApplePayPaymentMethod extends IPaymentMethod {
        config: IApplePayPaymentMethodConfig;
        code: Methods.ApplePay;
    }

    export interface IApplePayPaymentMethodConfig extends IPaymentMethodConfig {
        merchantName: string;
    }

    export interface IApplePay extends IApi<IApplePayCreateOptions, IApplePayInstance> {}

    export interface IApplePayCreateOptions extends IApiCreateOptions {
        // Used in conjunction with authorization, allows the Apple Pay instance to be available right
        // away by fetching the client configuration in the background. When this option is used, ApplePay#createPaymentRequest
        // will return a promise that resolves with the configuration instead of returning synchronously.
        useDeferredClient?: boolean;
    }

    export interface IApplePayInstance extends IApiInstance {
        // A special merchant ID which represents the merchant association with Braintree.
        // Required when using ApplePaySession.canMakePaymentsWithActiveCard.
        merchantIdentifier: string;
        // Merges a payment request with Braintree defaults to return an {external:ApplePayPaymentRequest}.
        // The following properties are assigned to paymentRequest if not already defined.
        // Their default values come from the Braintree gateway.
        // -countryCode
        // -currencyCode
        // -merchantCapabilities
        // -supportedNetworks
        createPaymentRequest(requestData: IApplePayPaymentRequest): Promise<ApplePay.IPaymentRequest> | ApplePay.IPaymentRequest;
        // Validates your merchant website, as required by ApplePaySession before payment can be authorized.
        performValidation(options: IApplePayPerformValidationOptions, callback: (error, merchantSession) => void): void;
        performValidation(options: IApplePayPerformValidationOptions): Promise<any>;
        // Tokenizes an Apple Pay payment. This will likely be called in your ApplePaySession's onpaymentauthorized callback.
        tokenize(options: IApplePayTokenizeParams, callback: (tokenizeErr, payload: IApplePayTokenizePayload) => void): void;
        tokenize(options: IApplePayTokenizeParams): Promise<IApplePayTokenizePayload>;
    }

    export interface IApplePayPaymentRequest extends Partial<ApplePay.IPaymentRequest> {
        total: ApplePay.ILineItem;
    }

    export interface IApplePayPerformValidationOptions {
        // The validationURL fram an ApplePayValidateMerchantEvent.
        validationURL: string;
        // The canonical name for your store. Use a non-localized name.
        // This parameter should be a UTF-8 string that is a maximum of 128 characters.
        // The system may display this name to the user.
        displayName: string;
    }

    export interface IApplePayTokenizeParams {
        token: string;
    }

    export interface IApplePayTokenizePayload extends ITokenizePayload<TokenizePayloadTypes.APPLE_PAY, IApplePayTokenizePayloadDetails> {}

    export interface IApplePayTokenizePayloadDetails {
        // Type of card, ex: Visa, MasterCard.
        cardType: string;
        // The name of the card holder.
        cardHolderName: string;
        // Last two digits of card number.
        dpanLastTwo: string;
    }

    ////////////////////////////////////////////
    ///////////
    /////////// Google Pay
    ///////////
    ////////////////////////////////////////////

    export interface IGooglePayPaymentMethod extends IPaymentMethod {
        config: IGooglePayPaymentMethodConfig;
        code: Methods.GooglePay;
    }

    export interface IGooglePayPaymentMethodConfig extends IPaymentMethodConfig {
        cardTypes?: string[];
        environment: GooglePay.Environments;
        merchantId?: string;
    }

    export interface IGooglePay extends IApi<IGooglePayCreateOptions, IGooglePayInstance> {}

    export interface IGooglePayCreateOptions extends IApiCreateOptions {
        // Used in conjunction with authorization, allows the Google Payment instance to be available right away by fetching the client
        // configuration in the background. When this option is used,
        // GooglePayment#createPaymentDataRequest will return a promise that resolves
        // with the configuration instead of returning synchronously.
        useDeferredClient?: boolean;
        // The version of the Google Pay API to use. Value of 2 is required to accept parameters documented by Google.
        // Omit this parameter to use the deprecated Google Pay Version 1.
        googlePayVersion?: number;
        // A Google merchant identifier issued after your website is approved by Google.
        // Required when PaymentsClient is initialized with an environment property of PRODUCTION, but may be omitted in TEST environment.
        googleMerchantId?: string;
    }

    export interface IGooglePayInstance extends IApiInstance {
        parseResponse(response: GooglePay.IPaymentData, callback: (tokenizeErr, payload: IGoglePayTokenizePayload) => void): void;
        parseResponse(response: GooglePay.IPaymentData): Promise<IGoglePayTokenizePayload>;
        createPaymentDataRequest(overrides?: IGooglePayPaymentDataRequest): GooglePay.IPaymentDataRequest;
    }

    export interface IGoglePayTokenizePayload
        extends ITokenizePayload<TokenizePayloadTypes.GOOGLE_PAY | TokenizePayloadTypes.CREDIT_CARD, IGooglePayTokenizePayloadDetails> {}

    export interface IGooglePayTokenizePayloadDetails extends IPayloadDetails {
        // True if the card is network tokenized.
        isNetworkTokenized: boolean;
        // First six digits of card number.
        bin: string;
    }

    export interface IGooglePayPaymentDataRequest extends Partial<GooglePay.IPaymentDataRequest> {
        transactionInfo: GooglePay.ITransactionInfo;
    }
}
